'use strict';
'use es6';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getAvatarResolver = exports.getAvatarURI = exports.LAST_TRY_DELAY = exports.SECOND_TRY_DELAY = exports.FIRST_TRY_DELAY = undefined;
exports.makeCacheAtom = makeCacheAtom;
exports.makeQueueAtom = makeQueueAtom;
exports.queuer = queuer;
exports.makeQueuer = makeQueuer;
exports.toDimensionBuckets = toDimensionBuckets;
exports.toBatches = toBatches;
exports.flush = flush;
exports.makeAvatarResolver = makeAvatarResolver;

var _ = require('_');

var _Atom = require('./lib/Atom');

var Atom = _interopRequireWildcard(_Atom);

var _AvatarAPI = require('./api/AvatarAPI');

var _defaultFetchAvatars = require('./api/defaultFetchAvatars');

var _defaultFetchAvatars2 = _interopRequireDefault(_defaultFetchAvatars);

var _immutable = require('immutable');

var _invariant = require('react-utils/invariant');

var _invariant2 = _interopRequireDefault(_invariant);

var _hubspot = require('hubspot.PortalIdParser');

var _hubspot2 = _interopRequireDefault(_hubspot);

var _LookupRecord = require('./Records/LookupRecord');

var _LookupRecord2 = _interopRequireDefault(_LookupRecord);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) newObj[key] = obj[key]; } } newObj.default = obj; return newObj; } }

var FIRST_TRY_DELAY = exports.FIRST_TRY_DELAY = 200;
var SECOND_TRY_DELAY = exports.SECOND_TRY_DELAY = 1000;
var LAST_TRY_DELAY = exports.LAST_TRY_DELAY = 4000;

/**
 * Given the various state Atoms, retrieves a URI from the cache or adds the
 * lookup to the queue.
 *
 * @param  {Atom<Map<LookupRecord,?string>>}
 * @param  {Atom<Set<LookupRecord>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @param  {Lookup}
 * @return {?LookupRecord}
 */
function _getAvatarURI(cacheAtom, pendingAtom, enqueue, fetcher, portalId, lookup) {
  (0, _invariant2.default)(typeof fetcher === 'function', 'expected `fetcher` to be a function but got `%s`', fetcher);
  (0, _invariant2.default)(typeof portalId === 'number' || !lookup.requiresPortalId(), 'expected `portalId` to be a number or the lookup to not require a portalId but got `%s`', portalId);
  (0, _invariant2.default)(lookup instanceof _LookupRecord2.default, 'expected `lookup` to be a LookupRecord but got `%s`', lookup);

  var avatars = Atom.deref(cacheAtom);
  if (avatars.has(lookup)) {
    return avatars.get(lookup);
  }
  if (!Atom.deref(pendingAtom).has(lookup)) {
    enqueue(fetcher, portalId, _immutable.Set.of(lookup));
  }
  return undefined;
}

/**
 * @param  {?Map<LookupRecord, ?string>}
 * @return {Atom<Map<LookupRecord, ?string>>}
 */
exports.getAvatarURI = _getAvatarURI;
function makeCacheAtom() {
  var initialCache = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : (0, _immutable.Map)();

  return Atom.atom(initialCache, _immutable.Map.isMap);
}

/**
 * @param  {?Set<LookupRecord>}
 * @return {Atom<Set<LookupRecord>>}
 */
function makeQueueAtom() {
  var initialQueue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : (0, _immutable.Set)();

  return Atom.atom(initialQueue, _immutable.Set.isSet);
}

/**
 * Adds lookups to the queue and schedules a queue flush.
 *
 * @param  {Atom<Set<LookupRecord>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @param  {Set<LookupRecord>}
 * @return {void}
 */
function queuer(queueAtom, flushQueue, fetcher, portalId, lookups) {
  (0, _invariant2.default)(_immutable.Set.isSet(lookups) && !lookups.isEmpty(), 'expected `lookups` to be a non-empty Set but got `%s`', lookups);
  Atom.swap(queueAtom, function (current) {
    return current.union(lookups);
  });
  flushQueue(fetcher, portalId);
}

/**
 * Creates a queing function bound to a queue Atom.
 *
 * @param  {Function}
 * @param  {Function}
 * @param  {Atom<Map<LookupRecord,?string>>}
 * @param  {Atom<Set<LookupRecord>>}
 * @param  {Function}
 * @return {Function}
 */
function makeQueuer(queueAtom, doEnqueue, doFlush, cacheAtom, pendingAtom, nextEnqueue) {
  var flushQueue = (0, _.partial)(doFlush, cacheAtom, queueAtom, pendingAtom, nextEnqueue);
  return (0, _.partial)(doEnqueue, queueAtom, flushQueue);
}

/**
 * Splits lookups into buckets based on their dimensions so the fetcher can make
 * separate calls for each dimension.
 *
 * @param  {Set<LookupRecord>}
 * @return {Map<?number, Set(LookupRecord)>}
 */
function toDimensionBuckets(lookups) {
  return lookups.reduce(function (dimensionBuckets, lookup) {
    return dimensionBuckets.update(lookup.dimensions, function (maybeSet) {
      return typeof maybeSet === 'undefined' ? _immutable.Set.of(lookup) : maybeSet.add(lookup);
    });
  }, (0, _immutable.Map)());
}

/**
 *  Splits the lookup set into batches in order so the fetcher can make
 *  batch requests to Avatars
 *
 * @param  {Set<LookupRecord>}
 * @param  {number}
 * @return {List<Set<LookupRecord>>}
 */
function toBatches(lookupSet) {
  var batchSize = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 200;

  (0, _invariant2.default)(typeof batchSize === 'number' && batchSize > 0, 'expected `batchSize` to number greater than 0 but got `%s`', batchSize);
  return lookupSet.reduce(function (batches, lookup) {
    if (batches.last().size >= batchSize) {
      return batches.push(_immutable.Set.of(lookup));
    }
    return batches.set(-1, batches.last().add(lookup));
  }, _immutable.List.of((0, _immutable.Set)()));
}

/**
 * Try to resolve the contents of a queue, adding hits to the cache and pushing
 * retries to the next queue.
 *
 * @param  {Atom<Map<LookupRecord,?string>>}
 * @param  {Atom<Set<LookupRecord>>}
 * @param  {Atom<Set<LookupRecord>>}
 * @param  {Function}
 * @param  {Function}
 * @param  {number}
 * @return {void}
 */
function flush(cacheAtom, queueAtom, pendingAtom, nextFlush, fetcher, portalId) {
  var lookups = Atom.deref(queueAtom);
  if (lookups.isEmpty()) {
    return;
  }
  Atom.swap(pendingAtom, function (current) {
    return current.union(lookups);
  });
  Atom.swap(queueAtom, function (current) {
    return current.clear();
  });

  toDimensionBuckets(lookups).forEach(function (lookupSet, dimensions) {
    toBatches(lookupSet).forEach(function (batch) {
      (0, _AvatarAPI.fetchAvatars)(fetcher, portalId, batch, dimensions).then(function (_ref) {
        var retry = _ref.retry,
            updates = _ref.updates;

        Atom.swap(pendingAtom, function (current) {
          return current.subtract(lookups);
        });
        Atom.swap(cacheAtom, function (current) {
          return current.merge(updates);
        });
        if (nextFlush && !retry.isEmpty()) {
          nextFlush(fetcher, portalId, retry);
        }
      }).done();
    });
  });
}

/**
 * Factory that creates the various state Atoms and bound functions that compose
 * an AvatarResolver and cache.
 *
 * @return {Object}
 */
function makeAvatarResolver() {
  var cacheAtom = makeCacheAtom();
  var pendingAtom = makeQueueAtom();
  var firstQueueAtom = makeQueueAtom();
  var secondQueueAtom = makeQueueAtom();
  var lastQueueAtom = makeQueueAtom();

  var enqueueLastTry = makeQueuer(lastQueueAtom, queuer, (0, _.debounce)(flush, LAST_TRY_DELAY), cacheAtom, pendingAtom);

  var enqueueSecondTry = makeQueuer(secondQueueAtom, queuer, (0, _.debounce)(flush, SECOND_TRY_DELAY), cacheAtom, pendingAtom, enqueueLastTry);

  var enqueueFirstTry = makeQueuer(firstQueueAtom, queuer, (0, _.debounce)(flush, FIRST_TRY_DELAY), cacheAtom, pendingAtom, enqueueSecondTry);

  return {
    /**
     * The Atom that stores the lookup => url cache. It's usually best to use
     * the helper methods (getAvatarURI, setAvatarURI, etc.) rather than using
     * the cache directly.
     *
     * @prop  {Atom<Map<LookupRecord,?string>>}
     */
    avatars: cacheAtom,

    /**
     * Returns an avatar uri from the cache or queues the lookup to be fetched.
     *
     * @param  {LookupRecord}
     * @param  {Function}       fetcher that returns a promise for the API response
     * @param  {number}         portal id
     * @return {?string}        avatar url
     */
    getAvatarURI: function getAvatarURI(lookup) {
      var fetcher = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _defaultFetchAvatars2.default;
      var portalId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : _hubspot2.default.get();

      return _getAvatarURI(cacheAtom, pendingAtom, enqueueFirstTry, fetcher, portalId, lookup);
    },


    /**
     * Associate an avatar uri with a lookup in the cache. This is useful when
     * client interaction changes the image an avatar should display (i.e. the
     * user uploaded a new profile photo for a contact).
     *
     * @param  {LookupRecord}   lookup (email or domain)
     * @param  {?string}        new url or null
     * @param  {Function}       fetcher that returns a promise for the API response
     * @param  {number}         portal id
     * @return {?string}        avatar url
     */
    setAvatarURI: function setAvatarURI(lookup, value) {
      var key = lookup;
      (0, _invariant2.default)(key instanceof _LookupRecord2.default, 'expected `lookup` to be an instance of LookupRecord but got `%s`', lookup);
      (0, _invariant2.default)(value === null || typeof value === 'string' && value.length > 0, 'expected `value` to be a non-empty string or `null` but got `%s`', value);

      Atom.swap(cacheAtom, function (current) {
        return current.set(lookup, value).map(function (cachedValue, cachedLookup) {
          return lookup.isSameAs(cachedLookup) ? value : cachedValue;
        });
      });
    },


    /**
     * Register a callback to be triggered whenever the avatars caches changes.
     *
     * @param  {Function}  callback
     * @return void
     */
    watch: (0, _.partial)(Atom.watch, cacheAtom),

    /**
     * Unregister a callback that was previously registered with `watch`.
     *
     * @param  {Function}  callback
     * @return void
     */
    unwatch: (0, _.partial)(Atom.unwatch, cacheAtom)
  };
}

/**
 * Returns the AvatarResolver singleton used by UIAvatar.
 *
 * @return {Object}
 */
var getAvatarResolver = exports.getAvatarResolver = (0, _.once)(makeAvatarResolver);


//////////////////
// WEBPACK FOOTER
// /usr/share/hubspot/build/.hubspot/static-archive/ui-addon-avatars/static-2.493/js/AvatarResolver.js
// module id = 979
// module chunks = 2 3